import React, { useEffect, useRef } from "react";

import styled from "styled-components";

interface Props {
  isActive: boolean;
  zIndex?: number;
  opacity?: number;
  blur?: string;
  radius?: string;
  children?: React.ReactNode;
}

export const Backdrop: React.FC<Props> = (props) => {
  const { isActive, zIndex, opacity, blur, radius, children } = props;
  const backdropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isActive && backdropRef.current) {
      const node = backdropRef.current;
      const prevFocused = document.activeElement as HTMLElement | null;
      node.focus();

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Tab") {
          e.preventDefault();
        }
      };
      node.addEventListener("keydown", handleKeyDown);

      return () => {
        node.removeEventListener("keydown", handleKeyDown);
        prevFocused?.focus();
      };
    }
  }, [isActive]);

  if (!isActive) {
    return null;
  }

  return (
    <>
      {children}
      <BackdropLayout
        ref={backdropRef}
        tabIndex={isActive ? 0 : -1}
        aria-hidden={!isActive}
        $isActive={isActive}
        $zIndex={zIndex}
        $opacity={opacity}
        $blur={blur}
        $radius={radius}
      />
    </>
  );
};

const BackdropLayout = styled.div<{
  $isActive: boolean;
  $zIndex?: number;
  $opacity?: number;
  $blur?: string;
  $radius?: string;
}>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, ${(props) => props.$opacity || 0.4});
  opacity: ${(props) => (props.$isActive ? "1" : "0")};
  pointer-events: ${(props) => (props.$isActive ? "auto" : "none")};
  transition: opacity 300ms ease-in-out;
  z-index: ${(props) => props.$zIndex || 50};
  backdrop-filter: ${(props) =>
    props.$blur ? `blur(${props.$blur})` : "none"};
  border-radius: ${(props) => props.$radius || "0"};
`;
